import { Text, Flex, UnorderedList, ListItem, Center, Spinner, Box } from "@chakra-ui/react";
import UserAvatar from "components/elements/UserAvatar";
import RoleSelect from "./RoleSelect";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { DeactiveMemberDialog } from "./DeactiveMemberDialog";
import { ReactivateMemberDialog } from "./ReactivateMemberDialog";
import InviteButton from "./InviteButton";

export interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  banned_until: string | null;
  is_active: boolean;
  role?: {
    id: string;
    name: string;
  };
  invited: boolean;
}

const TeamMemberList = ({
  members,
  roles,
  onLoad,
  isLoading,
}: {
  members: TeamMember[] | undefined;
  roles: any[] | undefined;
  onLoad: () => void;
  isLoading: boolean;
}) => {
  const { user } = useTenant();

  const isAdmin = user.role?.name === "Administrator";

  const isUserDeactivated = (member: TeamMember) => {
    return member.is_active === false;
  };

  const sortedMembers = members?.sort((a, b) => {
    if (a.id === user.id) return -1;
    if (b.id === user.id) return 1;
    return a.first_name.localeCompare(b.first_name);
  });

  if (isLoading) {
    return (
      <Center p={8}>
        <Spinner />
      </Center>
    );
  }

  return (
    <>
      <UnorderedList styleType="none" m={0} spacing={4}>
        {sortedMembers?.map(member => {
          const isDeactivated = isUserDeactivated(member);
          return (
            <ListItem
              key={member.id}
              p={4}
              bg="white"
              borderRadius="md"
              boxShadow="sm"
              border="1px solid"
              borderColor="gray.100"
              opacity={isDeactivated ? 0.6 : 1}
              position="relative"
            >
              <Flex
                direction={{ base: "column", md: "row" }}
                align={{ base: "flex-start", md: "center" }}
                justify="space-between"
                gap={4}
              >
                <Flex alignItems="center" gap={4} mb={{ base: 3, md: 0 }}>
                  <UserAvatar size="48px" user={member} />
                  <Box>
                    <Text fontWeight="medium" color={isDeactivated ? "gray.500" : "inherit"}>
                      {member.first_name} {member.last_name}
                    </Text>
                    <Text fontSize="sm" color={isDeactivated ? "gray.400" : "gray.500"}>
                      {member.email}
                    </Text>
                  </Box>
                </Flex>
                <Flex alignItems="center" gap={2} flexWrap="wrap">
                  <RoleSelect
                    member={member}
                    roles={roles}
                    onLoad={onLoad}
                    isDisabled={isDeactivated}
                  />
                  {isAdmin && member.id !== user.id && <InviteButton id={member.id} />}
                  {isAdmin && member.id !== user.id && (
                    <>
                      {!isDeactivated && (
                        <DeactiveMemberDialog userToDeactivate={member} onSuccess={onLoad} />
                      )}
                      {isDeactivated && (
                        <ReactivateMemberDialog userToReactivate={member} onSuccess={onLoad} />
                      )}
                    </>
                  )}
                </Flex>
              </Flex>
            </ListItem>
          );
        })}
      </UnorderedList>
    </>
  );
};

export default TeamMemberList;
